<script lang="ts">
	import { onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import type { GetStatsResponseHistoryData } from 'src/types/requests/charts/get_stats_period';
	import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
	import { devices_config } from '@devices/index';
	import { devices } from 'src/stores';
	import { Icon, Loader, Text } from '@Shelly/shelly-design-system';
	import EmWidgetState from '../EmWidgetState.svelte';
	import EmHeatmapHoverSurface from '../EmHeatmapHoverSurface.svelte';
	import { fetchWholeHouseEnergyHistory } from '../helpers/fetch_pv_energy_history';
	import { fetchDeviceEnergyStatistics } from '../helpers/fetch_device_energy_statistics';
	import { fetchEnergyStatistics } from '../helpers/fetch_energy_statistics';
	import {
		buildDemoHeatmapHistory,
		buildDemoMinimalComparisonHistory,
		buildMinimalEnergyHeatmapModel,
		formatHeatmapWhLabel,
		parseEnergyHistoryDate,
		type EnergyHeatmapMetric,
		type EnergyHeatmapScope,
	} from '../helpers/energy_heatmap_helpers';
	import {
		buildHeatmapMonthControls,
		buildHeatmapTodayControls,
		buildHeatmapYesterdayControls,
	} from '../helpers/heatmap_period_helpers';
	import { getEnergyChartEmptyMessage } from '../helpers/energy_flow_helpers';
	import {
		ENERGY_ADD_DEVICE_HREF,
		getEnergyPvSettingsHref,
		hasPowerMeterDevices,
	} from '../helpers/energy_widget_state_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getPVSetupDevices } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
	import { getActivePvSetupType } from '../helpers/pv_flow_helpers';
	import { isEnergyMonitoringDemoMode } from '../helpers/energy_data_mode';
	import { energyMonitoringDataMode } from '../helpers/store';
	import type { EnergyMonitoringPeriodId } from '../helpers/period_helpers';
	import {
		buildEnergyHeatmapCacheKey,
		buildHeatmapRefreshControls,
		energyHeatmapCacheCoversRange,
		mergeEnergyHeatmapHistory,
		readEnergyHeatmapCache,
		writeEnergyHeatmapCache,
	} from '../helpers/energy_heatmap_cache';
	import { currentUnitId } from 'src/stores/account/home';

	export let staticPreview = false;
	export let metric: EnergyHeatmapMetric = 'consumption';
	export let widgetTitle = '';
	export let scope: EnergyHeatmapScope = 'all';
	export let roomId: number | undefined;
	export let deviceId: string | undefined;

	let loading = true;
	let loadError = false;
	let monthHistory: GetStatsResponseHistoryData[] = [];
	let comparisonHistory: GetStatsResponseHistoryData[] = [];
	let statisticCostLabel: string | null = null;
	let loadToken = 0;

	$: model = buildMinimalEnergyHeatmapModel(monthHistory, comparisonHistory, metric);
	$: displayCells = model.cells.map((cell, index) =>
		staticPreview && cell.isCurrentMonth && !cell.isFuture
			? {
					...cell,
					value: cell.value || 420 + (index % 6) * 190,
					intensity: cell.intensity || (index % 5) + 1,
				}
			: cell,
	);
	$: weekLabels = displayCells
		.filter((_, index) => index % 7 === 0)
		.map((cell) => String(cell.date.getDate()));
	$: totalDisplay = staticPreview ? { value: '28.4', unit: 'kWh' } : splitValueLabel(model.totalLabel);
	$: totalCostLabel = staticPreview
		? '6.12'
		: model.totalCostLabel !== '—'
			? model.totalCostLabel
			: (statisticCostLabel ?? '—');
	$: hasData = displayCells.some((cell) => cell.value > 0);
	$: pvSetupDevices = getPVSetupDevices(getActivePvSetupType());
	$: hasCompletePvSetup =
		pvSetupDevices.gridDevices.length > 0 && pvSetupDevices.solarDevices.length > 0;
	$: resolvedWidgetTitle =
		widgetTitle || $format('dash_energy_heatmap_minimal_title', { default: 'Monthly Heatmap' });
	$: metricLabel =
		metric === 'generation'
			? $format('energy_monitoring_flow_generation', { default: 'Generation' })
			: $format('chart_legend_consumption', { default: 'Consumption' });
	$: colorScheme =
		metric === 'generation'
			? [
					'color-mix(in srgb, var(--color-chart-generated-energy) 18%, transparent)',
					'color-mix(in srgb, var(--color-chart-generated-energy) 38%, transparent)',
					'color-mix(in srgb, var(--color-chart-generated-energy) 58%, transparent)',
					'color-mix(in srgb, var(--color-chart-generated-energy) 78%, transparent)',
					'var(--color-chart-generated-energy)',
				]
			: [
					'color-mix(in srgb, var(--color-chart-consumed-energy) 18%, transparent)',
					'color-mix(in srgb, var(--color-chart-consumed-energy) 38%, transparent)',
					'color-mix(in srgb, var(--color-chart-consumed-energy) 58%, transparent)',
					'color-mix(in srgb, var(--color-chart-consumed-energy) 78%, transparent)',
					'var(--color-chart-consumed-energy)',
				];
	$: emptyMessage = getEnergyChartEmptyMessage($devices, (key, fallback) =>
		$format(key, { default: fallback }),
	);
	$: errorMessage = $format('energy_monitoring_heatmap_load_error', {
		default: 'Energy history could not be loaded. Please try again.',
	});
	$: trendLabel =
		model.trendPercent === null
			? $format('dash_energy_heatmap_no_yesterday_comparison', {
					default: 'No comparison with yesterday',
				})
			: $format('dash_energy_heatmap_vs_yesterday', {
					default: '{value}% vs yesterday',
					values: {
						value: `${model.trendPercent > 0 ? '+' : ''}${model.trendPercent}`,
					},
				});

	const loadData = async (unitId: string) => {
		const token = ++loadToken;
		const scopeKey =
			scope === 'device'
				? `device:${deviceId ?? ''}`
				: scope === 'room'
					? `room:${roomId ?? ''}`
					: 'all';
		const monthControls = buildHeatmapMonthControls();
		const monthBucket = `${monthControls.time.from.getFullYear()}-${String(
			monthControls.time.from.getMonth() + 1,
		).padStart(2, '0')}`;
		const cacheKey = buildEnergyHeatmapCacheKey(unitId, scopeKey, 'month', monthBucket);
		const cachedMonthHistory = readEnergyHeatmapCache(cacheKey);
		const hasCompleteCache = energyHeatmapCacheCoversRange(cacheKey, monthControls);
		if (cachedMonthHistory.length) monthHistory = cachedMonthHistory;
		loading = cachedMonthHistory.length === 0;
		loadError = false;

		try {
			if (staticPreview || isEnergyMonitoringDemoMode()) {
				monthHistory = buildDemoHeatmapHistory();
				comparisonHistory = buildDemoMinimalComparisonHistory();
				statisticCostLabel = null;
				return;
			}

			const fetchScoped = (
				controls: ChartControlsDataInterface,
				periodId: EnergyMonitoringPeriodId,
			) => {
				if (scope === 'device' && deviceId) {
					const device = $devices[deviceId];
					if (!device || !devices_config[device.type]?.isPM) return Promise.resolve(null);
					return fetchDeviceEnergyStatistics(device, controls, periodId, {
						mergeLastHour: true,
						requireChartData: false,
					});
				}
				if (scope === 'room' && roomId != null) {
					return fetchEnergyStatistics(controls, periodId, roomId, {
						mergeLastHour: true,
						requireChartData: false,
					});
				}
				return fetchWholeHouseEnergyHistory(controls, periodId);
			};
			const [monthResult, yesterdayResult, todayResult] = await Promise.all([
				fetchScoped(
					buildHeatmapRefreshControls(monthControls, hasCompleteCache),
					hasCompleteCache ? 'day' : 'month',
				),
				fetchScoped(buildHeatmapYesterdayControls(), 'day'),
				fetchScoped(buildHeatmapTodayControls(), 'day'),
			]);
			if (token !== loadToken) return;
			monthHistory = mergeEnergyHeatmapHistory(
				cachedMonthHistory,
				monthResult?.history ?? [],
			).filter((point) => {
				const timestamp = parseEnergyHistoryDate(point.datetime).getTime();
				return (
					timestamp >= monthControls.time.from.getTime() &&
					timestamp <= monthControls.time.to.getTime()
				);
			});
			writeEnergyHeatmapCache(cacheKey, monthHistory, monthControls);
			statisticCostLabel = monthResult?.statistic?.totalCost ?? null;
			comparisonHistory = [...(yesterdayResult?.history ?? []), ...(todayResult?.history ?? [])];
		} catch {
			if (token === loadToken) {
				if (!cachedMonthHistory.length) monthHistory = [];
				comparisonHistory = [];
				statisticCostLabel = null;
				loadError = cachedMonthHistory.length === 0;
			}
		} finally {
			if (token === loadToken) loading = false;
		}
	};

	$: {
		$currentUnitId;
		$energyMonitoringDataMode;
		staticPreview;
		scope;
		roomId;
		deviceId;
		void loadData($currentUnitId);
	}

	onDestroy(() => {
		loadToken += 1;
	});

	const cellColor = (intensity: number): string =>
		intensity > 0 ? colorScheme[Math.min(intensity - 1, colorScheme.length - 1)] : '';

	const refresh = () => loadData($currentUnitId);

	function splitValueLabel(label: string): { value: string; unit: string } {
		const separatorIndex = label.lastIndexOf(' ');
		return separatorIndex > 0
			? { value: label.slice(0, separatorIndex), unit: label.slice(separatorIndex + 1) }
			: { value: label, unit: '' };
	}
</script>

<section class="em-minimal-heatmap">
	<div class="em-minimal-heatmap-heading">
		<div>
			<Text styleType="h2" class="text-600">{resolvedWidgetTitle}</Text>
			<Text styleType="miniature" class="text-color-muted">{model.monthLabel}</Text>
		</div>
	</div>

	{#if loading}
		<div class="em-minimal-heatmap-state">
			<Loader size="m" />
		</div>
	{:else if loadError}
		<EmWidgetState kind="error" message={errorMessage} minHeight="240px" onRefresh={refresh} />
	{:else if !staticPreview && metric === 'generation' && !hasCompletePvSetup}
		<EmWidgetState
			kind="setup"
			message={$format('energy_monitoring_pv_setup_missing', {
				default: 'Set up grid and solar meters to see generated energy.',
			})}
			minHeight="240px"
			actionHref={getEnergyPvSettingsHref($showNewLayout)}
			actionLabel={$format('energy_monitoring_pv_configure', { default: 'Set up PV' })}
		/>
	{:else if !hasData}
		<EmWidgetState
			kind={hasPowerMeterDevices($devices) ? 'empty' : 'setup'}
			message={emptyMessage}
			minHeight="240px"
			actionHref={!hasPowerMeterDevices($devices) ? ENERGY_ADD_DEVICE_HREF : ''}
			actionLabel={!hasPowerMeterDevices($devices)
				? $format('button_add_device', { default: 'Add device' })
				: ''}
		/>
	{:else}
		<div class="em-minimal-heatmap-main">
			<div class="em-minimal-heatmap-calendar-panel">
				<EmHeatmapHoverSurface>
					<div
						class="em-minimal-heatmap-calendar"
						role="img"
						aria-label={$format('dash_energy_heatmap_month_calendar_label', {
							default: '{month} {metric} heatmap',
							values: { month: model.monthLabel, metric: metricLabel },
						})}
					>
						<div class="em-minimal-heatmap-weekdays" aria-hidden="true">
							{#each ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as weekday}
								<Text styleType="miniature" class="text-color-muted em-minimal-axis-text"
									>{weekday}</Text
								>
							{/each}
						</div>
						<div class="em-minimal-heatmap-week-labels" aria-hidden="true">
							{#each weekLabels as label}
								<Text styleType="miniature" class="text-color-muted em-minimal-axis-text"
									>{label}</Text
								>
							{/each}
						</div>
						<div class="em-minimal-heatmap-grid" aria-hidden="true">
							{#each displayCells as cell (cell.date.getTime())}
								<span
									class="em-minimal-heatmap-cell"
									class:em-minimal-heatmap-cell-outside={!cell.isCurrentMonth ||
										cell.isFuture}
									style={cell.intensity > 0 && cell.isCurrentMonth && !cell.isFuture
										? `--em-minimal-heatmap-cell:${cellColor(cell.intensity)}`
										: undefined}
									data-em-heatmap-tooltip={`${cell.label}: ${formatHeatmapWhLabel(cell.value)}`}
								>
									{#if cell.value > 0 && cell.isCurrentMonth && !cell.isFuture}
										<span class="em-minimal-heatmap-cell-value">
											{formatHeatmapWhLabel(cell.value)}
										</span>
									{/if}
								</span>
							{/each}
						</div>
					</div>
				</EmHeatmapHoverSurface>

				<div class="em-minimal-heatmap-legend">
					<Text styleType="miniature" class="text-color-muted">
						{$format('option_low', { default: 'Low' })}
					</Text>
					<div class="em-minimal-heatmap-legend-swatches" aria-hidden="true">
						{#each colorScheme as color}
							<span style={`background:${color}`}></span>
						{/each}
					</div>
					<Text styleType="miniature" class="text-color-muted">
						{$format('option_high', { default: 'High' })}
					</Text>
				</div>
			</div>

			<div class="em-minimal-heatmap-kpis">
				<div class="em-minimal-heatmap-kpi">
					<div class="em-minimal-heatmap-value">
						<Text styleType="h1" class="text-600 em-minimal-kpi-value"
							>{totalDisplay.value}</Text
						>
						{#if totalDisplay.unit}
							<Text styleType="body">{totalDisplay.unit}</Text>
						{/if}
					</div>
					<Text styleType="body-small" class="text-color-muted em-minimal-kpi-label">
						{$format('dash_energy_heatmap_total_metric', {
							default: 'Total {metric}',
							values: { metric: metricLabel },
						})}
					</Text>
				</div>
				<div class="em-minimal-heatmap-kpi">
					<div class="em-minimal-heatmap-value">
						<Text styleType="h1" class="text-600 em-minimal-kpi-value">{totalCostLabel}</Text
						>
					</div>
					<Text styleType="body-small" class="text-color-muted em-minimal-kpi-label">
						{$format('energy_monitoring_total_cost', { default: 'Total cost' })}
					</Text>
				</div>
				<div class="em-minimal-heatmap-kpi">
					<div class="em-minimal-heatmap-value">
						<Text styleType="h1" class="text-600 em-minimal-kpi-value" noWrap>
							<span class="em-minimal-peak-value-full">
								{$format('dash_energy_heatmap_peak_hour_value', {
									default: 'Peak {time}',
									values: { time: staticPreview ? '18:00' : model.peakHourLabel },
								})}
							</span>
							<span class="em-minimal-peak-value-compact">
								{staticPreview ? '18:00' : model.peakHourLabel}
							</span>
						</Text>
					</div>
					<Text styleType="body-small" class="text-color-muted em-minimal-kpi-label">
						{$format('dash_energy_heatmap_peak_time', { default: 'Peak time' })}
					</Text>
				</div>
			</div>
		</div>

		<div class="em-minimal-heatmap-trend">
			<Icon
				icon={model.trendDirection === 'down'
					? 'fas fa-arrow-trend-down'
					: 'fas fa-arrow-trend-up'}
				size="s"
				class={model.trendDirection === 'down'
					? 'em-minimal-heatmap-trend-down'
					: 'em-minimal-heatmap-trend-up'}
			/>
			<Text styleType="body-small" class="text-color-muted">{trendLabel}</Text>
		</div>
	{/if}
</section>

<style lang="scss">
	.em-minimal-heatmap {
		display: flex;
		flex-direction: column;
		width: 100%;
		min-width: 0;
		overflow: visible;
		container-type: inline-size;
		container-name: minimal-heatmap;
	}

	.em-minimal-heatmap-heading,
	.em-minimal-heatmap-trend {
		padding: var(--spacing-s);
	}

	.em-minimal-heatmap-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		border-bottom: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-minimal-heatmap-heading > div {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
	}

	.em-minimal-heatmap-state {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 240px;
	}

	.em-minimal-heatmap-main {
		display: grid;
		grid-template-columns: minmax(0, 3fr) minmax(calc(var(--spacing-xl) * 4), 2fr);
		flex: 1;
		min-width: 0;
		min-height: 0;
		border-bottom: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-minimal-heatmap-calendar-panel {
		container-type: inline-size;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-s);
		min-width: 0;
		padding: var(--spacing-s);
	}

	.em-minimal-heatmap-kpis {
		display: grid;
		grid-template-rows: repeat(3, minmax(0, 1fr));
		min-width: 0;
		border-left: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-minimal-heatmap-kpi {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-xs);
		min-width: 0;
		padding: var(--spacing-xs) var(--spacing-s);
		text-align: center;
	}

	.em-minimal-heatmap-kpi + .em-minimal-heatmap-kpi {
		border-top: 1px solid rgba(255, 255, 255, 0.12);
	}

	.em-minimal-heatmap-value {
		display: flex;
		align-items: baseline;
		justify-content: center;
		gap: var(--spacing-xs);
		min-width: 0;
	}

	.em-minimal-peak-value-compact {
		display: none;
	}

	.em-minimal-heatmap-calendar {
		--em-minimal-heatmap-cell-height: clamp(28px, 7cqi, 42px);
		display: grid;
		grid-template-columns: calc(var(--spacing-l) + var(--spacing-xs)) minmax(0, 1fr);
		grid-template-rows: auto auto;
		column-gap: var(--spacing-s);
		row-gap: var(--spacing-xs);
		width: min(100%, 640px);
		margin: 0 auto;
		box-sizing: border-box;
	}

	.em-minimal-heatmap-weekdays {
		display: grid;
		grid-column: 2;
		grid-row: 1;
		grid-template-columns: repeat(7, minmax(0, 1fr));
		gap: var(--spacing-xs);
		min-width: 0;
		padding-bottom: var(--spacing-xs);
		text-align: center;
	}

	.em-minimal-heatmap-week-labels {
		display: grid;
		grid-column: 1;
		grid-row: 2;
		grid-template-rows: repeat(6, var(--em-minimal-heatmap-cell-height));
		gap: var(--spacing-xs);
		align-items: center;
		padding-right: var(--spacing-xs);
		text-align: right;
	}

	.em-minimal-heatmap-grid {
		display: grid;
		grid-column: 2;
		grid-row: 2;
		grid-template-columns: repeat(7, minmax(0, 1fr));
		grid-auto-rows: var(--em-minimal-heatmap-cell-height);
		gap: var(--spacing-xs);
	}

	.em-minimal-heatmap-cell {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		min-width: 0;
		padding: 2px;
		border-radius: var(--radius-extra-small);
		background: var(--em-minimal-heatmap-cell, var(--color-1));
		box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--color-base) 8%, transparent);
		box-sizing: border-box;
	}

	.em-minimal-heatmap-cell-value {
		display: block;
		max-width: 100%;
		overflow: hidden;
		color: white;
		font-size: clamp(0.5rem, 1.75cqi, 0.6875rem);
		font-weight: 600;
		line-height: 1;
		text-align: center;
		text-overflow: ellipsis;
		white-space: nowrap;
		text-shadow: 0 1px 2px rgba(0, 0, 0, 0.65);
	}

	.em-minimal-heatmap-cell-outside {
		background: color-mix(in srgb, var(--color-1) 45%, transparent);
		opacity: 0.55;
	}

	.em-minimal-heatmap-legend {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-s);
	}

	.em-minimal-heatmap-legend-swatches {
		display: flex;
		gap: var(--spacing-xs);
	}

	.em-minimal-heatmap-legend-swatches span {
		width: var(--spacing-l);
		height: var(--spacing-m);
		border-radius: var(--radius-small);
	}

	.em-minimal-heatmap-trend {
		display: flex;
		align-items: center;
		gap: var(--spacing-s);
	}

	.em-minimal-heatmap-trend :global(.em-minimal-heatmap-trend-up) {
		color: var(--color-primary);
	}

	.em-minimal-heatmap-trend :global(.em-minimal-heatmap-trend-down) {
		color: var(--color-danger);
	}

	@container minimal-heatmap (max-width: 420px) {
		.em-minimal-heatmap-calendar {
			--em-minimal-heatmap-cell-height: clamp(26px, 7cqi, 32px);
			grid-template-columns: calc(var(--spacing-l) + var(--spacing-xxs)) minmax(0, 1fr);
			column-gap: var(--spacing-xs);
		}

		:global(.em-minimal-axis-text) {
			font-size: 0.625rem;
		}

		:global(.em-minimal-kpi-value) {
			font-size: 1.25rem;
			line-height: 1.2;
		}

		.em-minimal-heatmap-heading,
		.em-minimal-heatmap-calendar-panel,
		.em-minimal-heatmap-trend {
			padding-right: var(--spacing-s);
			padding-left: var(--spacing-s);
		}

		.em-minimal-heatmap-kpi {
			padding: 0 var(--spacing-xs);
		}

		.em-minimal-heatmap-weekdays,
		.em-minimal-heatmap-week-labels,
		.em-minimal-heatmap-grid {
			gap: var(--spacing-xxs);
		}

		.em-minimal-heatmap-weekdays {
			padding-bottom: var(--spacing-xxs);
		}

		.em-minimal-heatmap-week-labels {
			padding-right: var(--spacing-xxs);
		}

		.em-minimal-heatmap-legend-swatches {
			gap: var(--spacing-xxs);
		}

		.em-minimal-heatmap-legend-swatches span {
			width: var(--spacing-m);
			height: var(--spacing-s-plus);
		}
	}

	@container minimal-heatmap (max-width: 560px) {
		.em-minimal-heatmap-main {
			grid-template-columns: minmax(0, 1fr);
			flex: none;
		}

		.em-minimal-heatmap-calendar-panel {
			padding-bottom: var(--spacing-m);
		}

		.em-minimal-heatmap-kpis {
			grid-template-columns: repeat(3, minmax(0, 1fr));
			grid-template-rows: minmax(92px, auto);
			border-top: 1px solid rgba(255, 255, 255, 0.12);
			border-left: 0;
		}

		.em-minimal-heatmap-kpi + .em-minimal-heatmap-kpi {
			border-top: 0;
			border-left: 1px solid rgba(255, 255, 255, 0.12);
		}

		.em-minimal-heatmap-kpi {
			gap: var(--spacing-xxs);
			padding: var(--spacing-s) var(--spacing-xs);
		}

		.em-minimal-heatmap-value {
			flex-direction: column;
			align-items: center;
			gap: 0;
		}

		:global(.em-minimal-kpi-value) {
			font-size: 1rem;
			line-height: 1.15;
		}

		:global(.em-minimal-kpi-label) {
			font-size: 0.6875rem;
			line-height: 1.2;
		}

		.em-minimal-peak-value-full {
			display: none;
		}

		.em-minimal-peak-value-compact {
			display: inline;
		}
	}
</style>
